import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { OTA_MODE_CHART_COLORS, OTA_MODE_OPTIONS, OtaMode } from '../../utils/otaMode';

interface GroupOtaStats {
  group: string;
  total: number;
  none: number;
  optIn: number;
  force: number;
}

interface OtaStatusChartProps {
  apiBaseUrl: string;
  totalDevices: number;
  otaNone: number;
  otaOptIn: number;
  otaForce: number;
}

const emptyGroup = (letter: string): GroupOtaStats => ({
  group: letter,
  total: 0,
  none: 0,
  optIn: 0,
  force: 0,
});

const percent = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 100) : 0;

const OtaStatusChart: React.FC<OtaStatusChartProps> = ({
  apiBaseUrl,
  totalDevices,
  otaNone,
  otaOptIn,
  otaForce,
}) => {
  const [groupStats, setGroupStats] = useState<GroupOtaStats[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

  useEffect(() => {
    const fetchGroupStats = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/devices/group-metrics`, {
          withCredentials: true,
        });
        const rows = Array.isArray(response.data) ? response.data : [];
        setGroupStats(rows.map((row: Partial<GroupOtaStats> & { markedForUpdate?: number }) => {
          const total = Number(row.total) || 0;
          const optIn = Number(row.optIn) || 0;
          const force = Number(row.force) || 0;
          const none = Number(row.none) || Math.max(total - optIn - force, 0);
          return {
            group: String(row.group || ''),
            total,
            none,
            optIn,
            force,
          };
        }));
        setError(null);
      } catch (err) {
        console.error('Error fetching group statistics:', err);
        setError('Failed to load group update statistics');
        setGroupStats([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGroupStats();
  }, [apiBaseUrl]);

  const overall = [
    { mode: 'none' as OtaMode, count: otaNone },
    { mode: 'opt-in' as OtaMode, count: otaOptIn },
    { mode: 'force' as OtaMode, count: otaForce },
  ];

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">OTA Status</h5>
        <div className="text-muted small">None, opt-in, and force across the fleet</div>
      </div>
      <div className="card-body">
        <div className="mb-4">
          <div className="d-flex justify-content-between mb-1">
            <span>All Devices</span>
            <span className="fw-bold">{totalDevices.toLocaleString()}</span>
          </div>
          <div className="progress" style={{ height: '25px' }}>
            {overall.map(({ mode, count }) => {
              const width = percent(count, totalDevices);
              if (count <= 0) return null;
              return (
                <div
                  key={mode}
                  className="progress-bar"
                  role="progressbar"
                  style={{
                    width: `${width}%`,
                    backgroundColor: OTA_MODE_CHART_COLORS[mode],
                    color: mode === 'none' ? '#495057' : '#212529',
                    fontWeight: 600,
                  }}
                  aria-valuenow={width}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  title={`${OTA_MODE_OPTIONS.find((option) => option.value === mode)?.label}: ${count.toLocaleString()} (${width}%)`}
                >
                  {width >= 8 ? `${width}%` : ''}
                </div>
              );
            })}
          </div>
          <OtaModeLegend />
        </div>

        <div>
          <div className="d-flex justify-content-between mb-1">
            <span>By Group</span>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <div className="d-flex justify-content-between" style={{ height: '200px' }}>
              {alphabet.map((letter) => {
                const groupData = groupStats.find((g) => g.group === letter) || emptyGroup(letter);
                const segments = [
                  { mode: 'none' as OtaMode, count: groupData.none },
                  { mode: 'opt-in' as OtaMode, count: groupData.optIn },
                  { mode: 'force' as OtaMode, count: groupData.force },
                ];
                const title = [
                  `Group ${letter}`,
                  `None ${groupData.none.toLocaleString()}`,
                  `Opt-in ${groupData.optIn.toLocaleString()}`,
                  `Force ${groupData.force.toLocaleString()}`,
                ].join(' · ');

                return (
                  <div
                    key={letter}
                    className="d-flex flex-column align-items-center"
                    style={{ width: `${100 / 26}%` }}
                  >
                    <div
                      className="h-100 w-75 d-flex flex-column"
                      style={{ minWidth: '12px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#f8f9fa' }}
                      title={title}
                    >
                      {groupData.total > 0 ? (
                        segments.map(({ mode, count }) => (
                          count > 0 ? (
                            <div
                              key={mode}
                              style={{
                                flex: count,
                                backgroundColor: OTA_MODE_CHART_COLORS[mode],
                                minHeight: count > 0 ? 2 : 0,
                              }}
                            />
                          ) : null
                        ))
                      ) : null}
                    </div>
                    <div className="mt-2 text-center">
                      <div className="fw-bold">{letter}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OtaModeLegend: React.FC = () => (
  <div className="d-flex flex-wrap gap-3 mt-2 small text-muted">
    {OTA_MODE_OPTIONS.map((option) => (
      <span key={option.value} className="d-inline-flex align-items-center gap-1">
        <span
          style={{
            width: '0.75rem',
            height: '0.75rem',
            borderRadius: '2px',
            backgroundColor: OTA_MODE_CHART_COLORS[option.value],
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        />
        {option.label}
      </span>
    ))}
  </div>
);

export default OtaStatusChart;
